import { Report } from '@arra/core';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { authenticatedFetch } from '../../authentication/authenticatedFetch';

const getReports = async (filter: any): Promise<Report[]> => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/annual-reports?${new URLSearchParams(
      filter,
    ).toString()}`,
  );
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return await response.json();
};

const removeReport = async (reportId: string): Promise<Report> => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/annual-reports/${reportId}`,
    {
      method: 'DELETE',
    },
  );
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return await response.json();
};

const createReport = async ({
  projectIds,
  name,
}: {
  projectIds: string[];
  name: string;
}) => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/annual-reports`,
    {
      method: 'POST',
      body: JSON.stringify({ projectIds, name }),
    },
  );
  if (response.status < 200 || response.status >= 300) {
    throw new Error(response.statusText);
  }
  return await response.json();
};

const useReports = (filter?: any) => {
  const queryClient = useQueryClient();

  const reportsQuery = useQuery(
    ['annual-reports', JSON.stringify(filter)],
    () => getReports(filter),
    {
      useErrorBoundary: true,
    },
  );

  const createReportMutation = useMutation(
    ({ projectIds, name }: { projectIds: string[]; name: string }) =>
      createReport({ projectIds, name }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['annual-reports']);
      },
    },
  );

  const removeReportMutation = useMutation(
    (reportId: string) => removeReport(reportId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['annual-reports']);
      },
    },
  );
  return {
    reports: reportsQuery.data,
    loading:
      reportsQuery.isLoading ||
      createReportMutation.isLoading ||
      removeReportMutation.isLoading,
    create: createReportMutation.mutateAsync,
    remove: removeReportMutation.mutateAsync,
  };
};

export default useReports;
